import React, { useRef } from 'react';
import Table from 'cccisd-table';
import Modal from 'cccisd-modal';
import Tooltip from 'cccisd-tooltip';
import style from './style.css';
import IconEdit from 'cccisd-icons/pencil5';
import IconDelete from 'cccisd-icons/bin';
import IconBell from 'cccisd-icons/bell2';
import IconClipboard from 'cccisd-icons/clipboard3';
import IconReload from 'cccisd-icons/loop3';
import notification from 'cccisd-notification';
import confirm from 'cccisd-confirm';
import axios from 'cccisd-axios';
import DeploymentEdit from './DeploymentEdit';
import assessmentReportTemplate from '../../vendor/reports/reportTemplates/AssessmentReport';
import _get from 'lodash/get';

const Boilerplate = window.cccisd.boilerplate;
const reportList = _get(window, 'cccisd.appDefs.reports.list', []);

const ClosedLinksTable = ({ deployments, onSuccess }) => {
    const editModalRefs = useRef({});
    const reportModalRefs = useRef({});
    const assessmentReport = reportList.find(item => item.id === 'assessment') || null;
    const postAssessmentReport = reportList.find(item => item.id === 'postAssessment') || null;
    const ReportPlayer = assessmentReportTemplate.player;

    const tableData = deployments.map(row => ({
        ...row,
        siteName: row?.settings?.siteName || '',
    }));

    const getClosedTableColumns = () => {
        return [
            {
                handle: 'actions',
                label: 'Actions',
                name: 'actions',
                hideInCsv: true,
                render: (value, row) => {
                    return (
                        <div>
                            {row.opensAt && new Date(row.opensAt) > new Date() && (
                                <div className={style.actionButtons}>
                                    <div>
                                        <Modal
                                            ref={modalInstance => {
                                                editModalRefs.current[row.deploymentId] = modalInstance;
                                            }}
                                            size="medium"
                                            trigger={
                                                <Tooltip title="Edit Link Details">
                                                    <button
                                                        type="button"
                                                        className={`btn btn-sm btn-warning ${style.spaceLeft}`}
                                                    >
                                                        <IconEdit />
                                                    </button>
                                                </Tooltip>
                                            }
                                            title={`${row.settings.siteName} - ${row.label}`}
                                        >
                                            <DeploymentEdit
                                                row={row}
                                                allowOpenDateEdit
                                                closeModal={() =>
                                                    editModalRefs.current[row.deploymentId] &&
                                                    editModalRefs.current[row.deploymentId].close()
                                                }
                                                onSuccess={onSuccess}
                                            />
                                        </Modal>
                                    </div>
                                    <div>
                                        <Tooltip title="Delete">
                                            <a
                                                className="btn btn-sm btn-danger"
                                                onClick={() => {
                                                    confirm({
                                                        message: 'Confirm delete?',
                                                        description:
                                                            'Are you sure you want to delete plan ' +
                                                            " '" +
                                                            row.label +
                                                            "' for site " +
                                                            row.settings.siteName +
                                                            '?',
                                                        confirmLabel: 'Yes',
                                                        abortLabel: 'Cancel',
                                                        // }).then(this._deletePlan);
                                                    }).then(async () => {
                                                        const deploymentId = row.deploymentId;

                                                        const response = await axios.put(
                                                            Boilerplate.route(`api.assignmentDeployment.destroy`, {
                                                                deploymentId,
                                                            })
                                                        );

                                                        if (response.data.errors && response.data.errors.deployment) {
                                                            notification({
                                                                type: 'danger',
                                                                message: response.data.errors.deployment[0],
                                                            });
                                                        }

                                                        if (response.data.status === 'success') {
                                                            notification({
                                                                message: `Plan deleted for site '${row.settings.siteName}'`,
                                                                type: 'success',
                                                                duration: 8000,
                                                            });
                                                        }
                                                        onSuccess();
                                                    });
                                                }}
                                            >
                                                <IconDelete />
                                            </a>
                                        </Tooltip>
                                    </div>
                                </div>
                            )}
                            {row.opensAt && new Date(row.opensAt) < new Date() && (
                                <div className={style.actionButtons}>
                                    <div>
                                        <Modal
                                            ref={modalInstance => {
                                                reportModalRefs.current[row.deploymentId] = modalInstance;
                                            }}
                                            size="large"
                                            trigger={
                                                <Tooltip title={`${row.settings.type} - Assessment Report`}>
                                                    <button
                                                        type="button"
                                                        className={`btn btn-sm btn-primary ${style.spaceLeft}`}
                                                    >
                                                        <IconClipboard />
                                                    </button>
                                                </Tooltip>
                                            }
                                            title={`${row.settings.siteName} - ${row.label} Assessment Report`}
                                        >
                                            <>
                                                <ReportPlayer
                                                    deploymentId={row.deploymentId}
                                                    settings={
                                                        row.settings.type === 'post'
                                                            ? postAssessmentReport?.reportTemplateSettings
                                                            : assessmentReport?.reportTemplateSettings
                                                    }
                                                    siteName={row.settings.siteName}
                                                    reportType={row.settings.type === 'post' ? 'post' : 'pre'}
                                                    preDeploymentId={row.settings.preDeploymentId}
                                                />
                                            </>
                                        </Modal>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                handle: 'siteName',
                label: 'Site Name',
                name: 'siteName',
                className: 'text-center',
                sort: true,
                filter: true,
                filterSettings: {
                    type: 'text',
                    field: 'siteName',
                },
                render: value => {
                    return (
                        <div>
                            <p>{value}</p>
                        </div>
                    );
                },
            },
            {
                handle: 'label',
                label: 'Collection Details',
                name: 'label',
                sort: true,
                filter: true,
            },
            {
                handle: 'openDate',
                label: 'Open Date',
                name: 'opensAt',
                sort: true,
                filter: true,
                filterSettings: {
                    type: 'date',
                },
                render: (value, row) => {
                    // convert date to local timezone and format as M/D/YYYY h:mm A
                    const localDate = row.opensAt && new Date(row.opensAt).toLocaleString();
                    return (
                        <div>
                            <p>{localDate}</p>
                            {/* show bell icon if open date is in the future */}
                            {row.opensAt && new Date(row.opensAt) > new Date() && (
                                <div className={style.iconBell}>
                                    <Tooltip title="Not yet open." placement="right" type="default">
                                        <IconBell />
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                handle: 'closeDate',
                label: 'Close Date',
                name: 'closesAt',
                sort: true,
                filter: true,
                filterSettings: {
                    type: 'date',
                },
                render: (value, row) => {
                    const localDate = row.closesAt && new Date(row.closesAt).toLocaleString();
                    return (
                        <div>
                            <p>{localDate}</p>
                        </div>
                    );
                },
            },
            {
                handle: 'completedResponses',
                label: 'Completion Rate',
                name: 'completedResponses',
                sort: true,
                filter: true,
                render: (value, row) => {
                    return (
                        <div>
                            <p>
                                {row.completedResponses} out of {row.totalResponses}
                            </p>
                        </div>
                    );
                },
            },
        ];
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '10px' }}>
                <h3 style={{ margin: 0 }}>Closed Links</h3>
                <button type="button" className="btn btn-secondary btn-sm" onClick={onSuccess}>
                    <IconReload /> Reload Closed Links
                </button>
            </div>
            <Table
                rowKey="deploymentId"
                name="closed-links-table"
                columns={getClosedTableColumns()}
                data={tableData}
                showPerPageOptions
                perPageOptions={[10, 25, 50, 100]}
                perPage={10}
                isCsvDownload
                csvFilename="closed_links.csv"
                csvOmittedColumns={['actions']}
            />
        </div>
    );
};

export default ClosedLinksTable;
